import React, { useState, useCallback, useEffect } from 'react';
import Modal from 'react-modal';
import { debounce } from 'lodash';
import MyButton from '../basic/MyButton';
import MyTextInput from '../basic/MyTextInput';
import MyCustomIcon from '../basic/MyCustomIcon';
import MyLoading from '../basic/MyLoading';
import { sendInvitation, checkEmailExistence } from '../../api';
import './ModalGrowCommunity.css';

Modal.setAppElement('#root');

function ModalGrowCommunity({ isOpen, onClose }) {
    const [formData, setFormData] = useState({
        senderName: '',
        inviteeName: '',
        inviteeEmail: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [emailMessage, setEmailMessage] = useState('');
    const [isValidEmail, setIsValidEmail] = useState(false);
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    const [emailExists, setEmailExists] = useState(false);

    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    useEffect(() => {
        if (isOpen) {
            setFormData({ senderName: '', inviteeName: '', inviteeEmail: '' });
            setError('');
            setEmailMessage('');
            setIsValidEmail(false);
            setIsCheckingEmail(false);
            setEmailExists(false);
        }
    }, [isOpen]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (name === 'inviteeEmail') {
            sequentialEmailCheck(value);
        }
    };

    const sequentialEmailCheck = useCallback(
        debounce(async (email) => {
            setIsCheckingEmail(false);
            setEmailExists(false);
            setEmailMessage('');
            setIsValidEmail(false);

            if (email.trim() === '') return;

            if (!EMAIL_REGEX.test(email)) {
                setEmailMessage('Please enter a valid email address');
                return;
            }

            setIsCheckingEmail(true);
            setEmailMessage('Checking email...');

            try {
                const { exists } = await checkEmailExistence(email);
                setEmailExists(exists);
                setEmailMessage(exists
                    ? 'This Email is already registered to PiggyBunker'
                    : 'Ready to invite.');
                setIsValidEmail(!exists);
            } catch (error) {
                console.error('Error checking email existence:', error);
                setEmailMessage('Error checking email. Please try again.');
            } finally {
                setIsCheckingEmail(false);
            }
        }, 300),
        []
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isValidEmail || emailExists) return;

        setIsLoading(true);
        setError('');

        try {
            await sendInvitation(formData.inviteeEmail, formData.inviteeName, formData.senderName);
            onClose();
            // You might want to show a success message here
        } catch (err) {
            console.error('Error sending invitation:', err);
            setError('Failed to send invitation. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="Grow Our Community"
            className="grow-community-modal"
            overlayClassName="grow-community-modal-overlay"
        >
            <h2>Grow Our Community</h2>
            <p>Help another family kickstart their kids' financial education journey!</p>
            <form onSubmit={handleSubmit}>
                <MyTextInput
                    label="Your Name"
                    name="senderName"
                    value={formData.senderName}
                    onChange={handleInputChange}
                    required
                />
                <MyTextInput
                    label="Friend's Name"
                    name="inviteeName"
                    value={formData.inviteeName}
                    onChange={handleInputChange}
                    required
                />
                <div className="input-group">
                    <MyTextInput
                        label="Friend's Email"
                        name="inviteeEmail"
                        type="email"
                        value={formData.inviteeEmail}
                        onChange={handleInputChange}
                        required
                    />
                    <div className="email-feedback">
                        {isCheckingEmail && <MyLoading size="small" />}
                        {emailMessage && !isCheckingEmail && (
                            <span className={isValidEmail ? "success-text" : "error-text"}>
                                {isValidEmail && <MyCustomIcon type="check" color="green" />}
                                {emailMessage}
                            </span>
                        )}
                    </div>
                </div>
                {error && <div className="error-message">{error}</div>}
                <div className="modal-actions">
                    <MyButton 
                        text="Send Invitation" 
                        type="submit" 
                        disabled={isLoading || !isValidEmail || emailExists}
                    />
                    <MyButton 
                        text="Cancel" 
                        onClick={onClose}
                        type="button"
                        backgroundColor="white"
                        color="#dc3545"
                        hoverBackgroundColor="#f8f8f8"
                        hoverColor="#c82333"
                        disabled={isLoading}
                    />
                </div>
            </form>
        </Modal>
    );
}

export default ModalGrowCommunity;